import type { CSSProperties } from 'react';
import type { ComponentCheck } from '../hooks/useTierEvaluation';

export const MACHINE_CHECK_FAIL_HINTS: Record<string, string> = {
  cpu: "This CPU isn't strong enough for this tier. Upgrade to unlock the higher earn rate.",
  gpu: "This GPU doesn't meet the requirements. A more powerful graphics card is needed.",
  memory: 'Not enough RAM for this tier. Adding more memory will help.',
  storage: 'Insufficient storage space. Free up disk space or add a larger drive.',
  network: 'Network speed is too slow. A faster internet connection is required.',
  videoEncoding: 'No hardware video encoder detected. A GPU with NVENC, AMF, or QuickSync is needed.',
  environment: 'Environment check failed. Virtual machines or flagged configurations may not qualify.',
  trust: 'Trust check failed. Ensure system integrity and security settings are correct.',
  location:
    'We don’t offer rewards outside the USA yet. Earning requires a PC located in the United States.',
};

const FAIL_HINT_BODY_STYLE: CSSProperties = {
  margin: '8px 0 0',
  color: '#8d8aa8',
  fontSize: 11,
  lineHeight: '16px',
  fontWeight: 500,
};

function MachineCheckFailHintBody({ component }: { component: string }) {
  if (component === 'osVersion') {
    return (
      <p style={FAIL_HINT_BODY_STYLE}>
        Playcast Rewards requires <strong style={{ fontWeight: 700 }}>Windows 11</strong> on this PC. Upgrade from
        Windows 10 to start earning.
      </p>
    );
  }

  const text =
    MACHINE_CHECK_FAIL_HINTS[component] ?? 'This component does not meet the requirement for this tier.';

  return <p style={FAIL_HINT_BODY_STYLE}>{text}</p>;
}

export function MachineCheckRow({
  check,
  isExpanded,
  onToggle,
  passLabel = 'Pass',
  failLabel = 'Fail',
  failHintText,
}: {
  check: ComponentCheck;
  isExpanded: boolean;
  onToggle: () => void;
  passLabel?: string;
  failLabel?: string;
  /** When set, overrides default component fail hints (rewards admin preview). */
  failHintText?: string;
}) {
  const isFail = !check.passes;
  return (
    <div
      style={{
        borderRadius: 8,
        border: '1px solid rgba(141,138,168,0.2)',
        background: '#0b0618',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => isFail && onToggle()}
        style={{
          width: '100%',
          padding: '8px 10px',
          border: 'none',
          background: 'transparent',
          cursor: isFail ? 'pointer' : 'default',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, width: '100%', whiteSpace: 'nowrap' }}>
          <span style={{ margin: 0, color: '#ffffff', fontSize: 12, lineHeight: '16px', fontWeight: 700, flexShrink: 0 }}>
            {check.label}
          </span>
          {check.currentValue ? (
            <span
              style={{
                margin: 0,
                color: '#8d8aa8',
                fontSize: 11,
                lineHeight: '16px',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                maxWidth: '100%',
                minWidth: 0,
                flex: 1,
              }}
            >
              {check.currentValue}
            </span>
          ) : (
            <span style={{ flex: 1 }} />
          )}
          <span
            style={{
              margin: 0,
              color: isFail ? 'rgb(252,107,131)' : 'rgb(134,239,172)',
              fontSize: 11,
              lineHeight: '16px',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {isFail ? failLabel : passLabel}
          </span>
          {isFail && (
            <span
              style={{
                margin: 0,
                color: '#8d8aa8',
                fontSize: 10,
                lineHeight: 1,
                fontWeight: 500,
                flexShrink: 0,
                transform: isExpanded ? 'rotate(180deg)' : 'none',
                display: 'inline-block',
              }}
            >
              ▼
            </span>
          )}
        </div>
      </button>
      {isExpanded && isFail && (
        <div style={{ padding: '0 10px 10px', borderTop: '1px solid rgba(141,138,168,0.12)' }}>
          {failHintText ? (
            <p style={FAIL_HINT_BODY_STYLE}>{failHintText}</p>
          ) : (
            <MachineCheckFailHintBody component={check.component} />
          )}
        </div>
      )}
    </div>
  );
}
